import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { OrganizationTheme } from "@tellescope/types-models";
import { getApiURL } from "../../config/api";
import { getPortalIdentifier } from "../../config/portal";

interface OrganizationStore {
  organization: OrganizationTheme | null;
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  fetchOrganization: () => Promise<void>;
  refetchWithBranding: (brandingSubdomain?: string) => Promise<void>;
}

/**
 * Store for organization info fetched via /organization-info endpoint.
 * This is loaded once on app initialization based on the current URL's subdomain/customPortalURL.
 * It's independent of user authentication and available to all parts of the app.
 */
export const useOrganizationStore = create<OrganizationStore>()(
  devtools(
    (set, get) => ({
      organization: null,
      isLoading: false,
      error: null,
      hasAttemptedFetch: false,

      fetchOrganization: async () => {
        // Prevent duplicate fetches
        if (get().hasAttemptedFetch || get().isLoading) {
          return;
        }

        set({ isLoading: true, error: null, hasAttemptedFetch: true });

        try {
          const portalIdentifier = getPortalIdentifier();
          const { subdomain, customPortalURL } = portalIdentifier;

          if (!subdomain && !customPortalURL) {
            throw new Error("Unable to determine organization from current URL");
          }

          const searchParams = new URLSearchParams();
          if (subdomain) searchParams.set("subdomain", subdomain);
          if (customPortalURL) searchParams.set("customPortalURL", customPortalURL);

          const response = await fetch(`${getApiURL()}/v1/organization-info?${searchParams.toString()}`);

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Failed to fetch organization info: ${response.status}`);
          }

          const organization: OrganizationTheme = await response.json();

          if (organization.name) {
            document.title = organization.name;
          }

          set({
            organization,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          console.error("Failed to fetch organization:", error);
          const errorMessage = error instanceof Error ? error.message : "Failed to fetch organization info";

          set({
            organization: null,
            isLoading: false,
            error: errorMessage,
          });
        }
      },

      refetchWithBranding: async (brandingSubdomain?: string) => {
        set({ isLoading: true, error: null });

        try {
          const portalIdentifier = getPortalIdentifier();
          const { subdomain: defaultSubdomain, customPortalURL } = portalIdentifier;

          // Use branding's subdomain if provided, otherwise fall back to default
          const subdomain = brandingSubdomain ?? defaultSubdomain;

          if (!subdomain && !customPortalURL) {
            throw new Error("Unable to determine organization from current URL");
          }

          const searchParams = new URLSearchParams();
          if (subdomain) searchParams.set("subdomain", subdomain);
          if (customPortalURL) searchParams.set("customPortalURL", customPortalURL);

          const response = await fetch(`${getApiURL()}/v1/organization-info?${searchParams.toString()}`);

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Failed to fetch organization info: ${response.status}`);
          }

          const organization: OrganizationTheme = await response.json();

          if (organization.name) {
            document.title = organization.name;
          }

          set({
            organization,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          console.error("Failed to fetch organization:", error);
          const errorMessage = error instanceof Error ? error.message : "Failed to fetch organization info";

          set({
            isLoading: false,
            error: errorMessage,
          });
        }
      },
    }),
    { name: "portal-organization-store" }
  )
);

// Convenience hooks
export const useOrganization = () => useOrganizationStore((state) => state.organization);
export const useOrganizationLoading = () => useOrganizationStore((state) => state.isLoading);
export const useOrganizationError = () => useOrganizationStore((state) => state.error);
export const useFetchOrganization = () => useOrganizationStore((state) => state.fetchOrganization);
export const useRefetchOrganizationWithBranding = () => useOrganizationStore((state) => state.refetchWithBranding);
